import * as React from "react";
import styled from "react-emotion";
import {Control, Field, Icon, Input, Label, Select} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import {getVideoToTextJobResult, startVideoToTextJob} from "../../../api/models/VideoToText";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import InputWarning from "../../../elements/InputWarning";
import Spacer from "../../../elements/Spacer";
import SectionName from "../../../elements/SectionName";
import InformationCard from "../../../elements/InformationCard";
import VideoToTextLanguages from "../../../components/model/VideoToText/VideoToTextLanguages";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {INVALID_FIELD_STYLE} from "../../../utils/Constants";

const JobErrorMessage = styled("div")`
  text-align: left;
  margin-top: 10px;
  font-size: 16px;
  color: red;
`;

interface IVideoToTextModelState {
    loading: boolean;
    languageCode: string;
    languages: any;
    urlInput: string;
    urlInputEmpty: boolean;
    urlInputInvalid: boolean;
    invalidYoutubeUrl: boolean;
    result: string;
    jobErrorMessage: string;
}

class VideoToTextModel extends React.Component<{}, IVideoToTextModelState> {

    public JOB_STATUS_CHECK_INTERVAL: number = 3000; // 3 seconds
    public JOB_STATUS_CHECK_MAX_COUNT: number = 100;
    public URL_REGEX = new RegExp('^(((http|https):\\/\\/)|(www\\.))[^ "]+$');
    public YOUTUBE_URL_REGEX = new RegExp('^((http|https):\\/\\/)((youtube\\.com\\/)|(www\\.youtube\\.com\\/)|(youtu\\.be\\/)|(m\\.youtube\\.com\\/))[^ "]+$');
    public YOUTUBE_URL_REGEX_2 = new RegExp('^((www\\.youtube\\.com\\/)|(www\\.youtu\\.be\\/)|(www\\.m\\.youtube\\.com\\/))[^ "]+$');

    public timeout:any = null;

    constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            languages: [],
            languageCode: "en-US",
            urlInput: "",
            urlInputEmpty: false,
            urlInputInvalid: false,
            invalidYoutubeUrl: false,
            result: "",
            jobErrorMessage: ""
        };
    }

    public async componentDidMount() {
        const languages = await VideoToTextLanguages.list();
        if (languages) {
            this.setState({
                languages
            });
        }
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.VIDEO_TO_TEXT);
    }

    public componentWillUnmount() {
        clearTimeout(this.timeout);
    }

    public handleLanguageCodeChange = (e: any) => {
        this.setState({
            languageCode: e.target.value
        });
    };

    public handleURLInputChange = (e: any) => {
        const value = e.target.value;
        let valid = true;
        let urlInputEmpty = false;
        let invalidYoutubeUrl = false;
        if (value.trim()) {
            valid = this.URL_REGEX.test(value.trim());
            if (valid) {
                invalidYoutubeUrl = !(this.YOUTUBE_URL_REGEX.test(value.trim()) || this.YOUTUBE_URL_REGEX_2.test(value.trim()));
            }
        } else {
            urlInputEmpty = true;
        }

        this.setState({
            urlInput: value,
            urlInputInvalid: !valid,
            invalidYoutubeUrl,
            urlInputEmpty
        });
    };

    public cancelChecking = (errorMessage: string) => {
        clearTimeout(this.timeout);
        this.setState({
            loading: false,
            result: "",
            jobErrorMessage: errorMessage
        });
    };

    public checkJobStatus = async (jobId: string, count: number) => {
        if (count > this.JOB_STATUS_CHECK_MAX_COUNT) {
            this.cancelChecking("Timeout error.");
            return;
        }

        const response = await getVideoToTextJobResult(jobId);
        if (response) {
            if (response.status === 210) {
                this.timeout = setTimeout(() => {
                    this.checkJobStatus(jobId, count + 1);
                }, this.JOB_STATUS_CHECK_INTERVAL);
            } else {
                const responseData = response.data;
                if (responseData && responseData.transcript) {
                    this.setState({
                        result: responseData.transcript,
                        jobErrorMessage: ""
                    });
                } else {
                    this.setState({
                        result: "",
                        jobErrorMessage: "Unable to transcribe."
                    });
                }

                this.setState({
                    loading: false
                });

                SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.VIDEO_TO_TEXT);
            }
        } else {
            this.cancelChecking("Error occurred.");
        }
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        if (!this.state.urlInput.trim() || this.state.urlInputInvalid || this.state.invalidYoutubeUrl || this.state.loading) {
            this.setState({result: ""});
            if (!this.state.urlInput.trim()) {
                this.setState({
                    urlInputEmpty: true,
                    urlInputInvalid: false,
                    invalidYoutubeUrl: false
                });
            }
            return;
        }

        this.setState({
            loading: true,
            result: "",
            jobErrorMessage: ""
        });

        let errorOccurred = false;

        const response = await startVideoToTextJob(this.state.urlInput.trim(), this.state.languageCode).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    jobErrorMessage: "Couldn't start process."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.status === 461) {
            this.setState({
                loading: false,
                jobErrorMessage: "Couldn't start process."
            });
            return;
        }

        if (response && response.data && response.data.job_id) {
            this.checkJobStatus(response.data.job_id, 1);
        } else {
            this.setState({
                loading: false,
                jobErrorMessage: "Couldn't start process."
            });
        }
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            urlInput: "",
            urlInputEmpty: false,
            urlInputInvalid: false,
            invalidYoutubeUrl: false,
            result: "",
            jobErrorMessage: ""
        });
        clearTimeout(this.timeout);
    };

    public render() {
        return <ModelContentContainer>
                <Card>
                    <Field>
                        <Label>Language</Label>
                        <Control>
                            <Select
                                onChange={this.handleLanguageCodeChange}
                                value={this.state.languageCode}
                            >
                                {
                                    this.state.languages.map((lang: any) => {
                                        return <option key={lang.code} value={lang.code}>{lang.name}</option>
                                    })
                                }
                            </Select>
                        </Control>
                    </Field>
                    <Field>
                        <Label>YouTube video URL</Label>
                        <Control>
                            <Input
                                style={(this.state.urlInputEmpty || this.state.urlInputInvalid || this.state.invalidYoutubeUrl)
                                    ? INVALID_FIELD_STYLE : undefined}
                                type="text"
                                placeholder="Enter video url here"
                                value={this.state.urlInput}
                                onChange={this.handleURLInputChange}
                            />
                            <div>
                                {
                                    this.state.urlInputEmpty && <InputWarning>Please input URL.</InputWarning>
                                }
                                {
                                    this.state.urlInputInvalid && <InputWarning>Please input valid URL.</InputWarning>
                                }
                                {
                                    this.state.invalidYoutubeUrl && <InputWarning>Please input valid YouTube URL.</InputWarning>
                                }
                            </div>
                        </Control>
                    </Field>
                    <Spacer/>
                    <div>
                        <Button onClick={this.onSubmit}>Submit</Button>
                        <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                    </div>
                    {this.state.loading && (
                        <div>
                            <Spacer/>
                            <span>Loading...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div>
                    )}
                    {!this.state.loading && this.state.result && (
                        <div>
                            <Spacer/>
                            <SectionName>Output</SectionName>
                            <InformationCard>
                                <div>{this.state.result}</div>
                            </InformationCard>
                        </div>
                    )}
                    {
                        this.state.jobErrorMessage && <JobErrorMessage>{this.state.jobErrorMessage}</JobErrorMessage>
                    }
                </Card>
            </ModelContentContainer>;
    }
}

export default VideoToTextModel;



// WEBPACK FOOTER //
// ./src/routes/Dashboard/VideoToText/VideoToTextModel.tsx